<template>
  <v-card :loading="isLoading" flat class="payment-monitoring-page">
    <v-card-title class="payment-monitoring-page-header align-start flex-wrap">
      <div>
        <h1 class="payment-monitoring-page-title">Ödeme Operasyon Özeti</h1>
        <div class="payment-monitoring-page-subtitle">
          Kredi kartı ödeme hacmini, ödeme döngüsel dağılımını ve servis
          sağlığını birlikte izleyin.
        </div>
      </div>
      <v-spacer />
      <v-btn small outlined color="primary" @click="loadOverview">
        <v-icon small left>mdi-refresh</v-icon>
        Yenile
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-0">
      <div class="payment-monitoring-context-row">
        <span class="payment-monitoring-context-chip">
          Seçilen tarih aralığı: <strong>{{ periodLabel }}</strong>
        </span>
        <span class="payment-monitoring-context-chip">
          Tutar kaynağı: <strong>Robosay kayıtları</strong>
        </span>
        <span
          :class="[
            'payment-monitoring-context-chip',
            overview.payment_globally_enabled
              ? 'payment-monitoring-context-chip--success'
              : 'payment-monitoring-context-chip--warning',
          ]"
        >
          {{
            overview.payment_globally_enabled
              ? "Yeni ödemeler açık"
              : "Yeni ödemeler kapalı"
          }}
        </span>
      </div>

      <payment-monitoring-filters
        v-model="filters"
        query-label="TYA ara"
        @apply="applyFilters"
      />

      <section class="payment-monitoring-section">
        <div class="payment-monitoring-section-heading">
          <div>
            <div class="payment-monitoring-section-title">Finansal özet</div>
            <div class="payment-monitoring-section-description">
              Başarı tarihi seçilen döneme düşen ödemelerin Robosay
              kayıtlarındaki tutarları.
            </div>
          </div>
          <v-chip x-small outlined>Sağlayıcı settlement teyidi içermez</v-chip>
        </div>
        <v-row dense>
          <v-col
            v-for="metric in financialMetrics"
            :key="metric.label"
            cols="12"
            sm="6"
            md="3"
          >
            <payment-monitoring-metric v-bind="metric" />
          </v-col>
        </v-row>
      </section>

      <v-card
        outlined
        class="payment-monitoring-panel payment-status-panel mb-5"
      >
        <div
          class="payment-monitoring-panel-header d-flex flex-wrap justify-space-between align-center"
        >
          <div>
            <div class="payment-monitoring-section-title">Ödeme durumu</div>
            <div class="payment-monitoring-section-description">
              Seçilen dönemde oluşan ödeme kayıtlarının son bilinen durumu.
            </div>
          </div>
          <v-btn
            small
            text
            color="primary"
            :to="{ name: 'admin.payment-ops.transactions' }"
          >
            Tüm ödemeler
            <v-icon small right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <v-row no-gutters class="payment-status-grid">
          <v-col
            v-for="status in paymentStatuses"
            :key="status.key"
            cols="12"
            sm="6"
            md="3"
          >
            <v-sheet
              class="payment-status-item"
              :class="`payment-status-item--${status.color}`"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="32"
                  :color="`${status.color} lighten-5`"
                  class="mr-3"
                >
                  <v-icon small :color="status.color">{{ status.icon }}</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-bold">{{ status.label }}</div>
                  <div class="caption text--secondary">
                    {{ status.caption }}
                  </div>
                </div>
                <v-spacer />
                <div class="text-h6 font-weight-bold">{{ status.value }}</div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card>

      <v-card outlined class="payment-monitoring-panel mb-5">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="payment-monitoring-section-title">
              Sağlayıcı performansı
            </div>
            <div class="payment-monitoring-section-description">
              Seçilen dönemin ölçülen ödeme ve attempt sonuçları.
            </div>
          </div>
          <v-spacer />
          <v-btn
            small
            text
            color="primary"
            :to="{ name: 'admin.payment-ops.providers' }"
          >
            Sağlayıcı detayları
          </v-btn>
        </v-card-title>
        <v-data-table
          :headers="providerHeaders"
          :items="overview.providers || []"
          :loading="isLoading"
          hide-default-footer
          disable-pagination
          class="elevation-0"
        >
          <template v-slot:item.new_payments_enabled="{ value }">
            <v-chip x-small dark :color="value ? 'success' : 'grey'">
              {{ value ? "Çalışıyor" : "Kapalı" }}
            </v-chip>
          </template>
          <template v-slot:item.success_rate="{ value }">
            {{ percent(value) }}
          </template>
          <template v-slot:item.charged_amount="{ value }">
            <rs-table-cell-number price :value="value" />
          </template>
          <template v-slot:item.attention_count="{ value }">
            <v-chip x-small :color="value ? 'warning' : 'success'" dark>
              {{ value }} kayıt
            </v-chip>
          </template>
          <template v-slot:item.last_success_at="{ value }">
            {{ formatDate(value) }}
          </template>
        </v-data-table>
      </v-card>

      <v-card outlined class="payment-monitoring-panel">
        <v-card-title class="payment-monitoring-panel-header">
          <div>
            <div class="subtitle-1 font-weight-bold">
              Dikkat gerektiren kayıtlar
            </div>
            <div class="caption text--secondary">
              Bekleyen, sonucu belirsiz veya manuel inceleme gerektiren ilk
              kayıtlar.
            </div>
          </div>
        </v-card-title>
        <v-data-table
          :headers="attentionHeaders"
          :items="overview.attention_payments || []"
          :loading="isLoading"
          hide-default-footer
          disable-pagination
          class="elevation-0"
        >
          <template v-slot:item.payment_id="{ item }">
            <strong>#{{ item.payment_id }}</strong>
            <div class="caption text--secondary">
              Deneme #{{ item.attempt_id || "-" }}
            </div>
          </template>
          <template v-slot:item.cluster_name="{ item }">
            <strong>{{ item.cluster_name }}</strong>
            <div class="caption text--secondary">
              TYA #{{ item.cluster_id }}
            </div>
          </template>
          <template v-slot:item.amount="{ value }">
            <rs-table-cell-number price :value="value" />
          </template>
          <template v-slot:item.monitoring_status="{ value }">
            <v-chip x-small dark :color="statusMeta(value).color">
              {{ statusMeta(value).text }}
            </v-chip>
          </template>
          <template v-slot:item.last_provider_event="{ item }">
            <span>{{
              item.provider_result || item.last_provider_event || "-"
            }}</span>
            <div class="caption text--secondary">
              {{
                formatDate(
                  item.last_provider_event_at || item.attempt_created_at
                )
              }}
            </div>
          </template>
        </v-data-table>
      </v-card>

      <div class="payment-monitoring-callout">
        <v-icon small color="info">mdi-information-outline</v-icon>
        <span>
          Sağlayıcı ekleme, aktif/pasif seçimi ve alt üye işyeri kurulumu bu
          izleme ekranında değil; mevcut TYA kredi kartı entegrasyonu alanında
          yönetilir.
        </span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { hasPermissions, isPage } from "@/view/mixins";
import PaymentMonitoringFilters from "./components/PaymentMonitoringFilters.vue";
import PaymentMonitoringMetric from "./components/PaymentMonitoringMetric.vue";
import {
  buildPaymentMonitoringParams,
  formatMonitoringDate,
  formatMonitoringPercent,
  paymentMonitoringFiltersFromQuery,
  syncPaymentMonitoringUrl,
} from "./paymentMonitoring";

const emptyOverview = () => ({
  meta: { period: {} },
  payment_globally_enabled: false,
  financial_summary: {},
  payment_status: {},
  providers: [],
  attention_payments: [],
});

export default {
  name: "PaymentOperationsOverview",
  components: { PaymentMonitoringFilters, PaymentMonitoringMetric },
  mixins: [hasPermissions, isPage],
  data() {
    return {
      pageMeta: { title: "Ödeme Operasyon Özeti" },
      isLoading: false,
      filters: paymentMonitoringFiltersFromQuery(this.$route.query),
      overview: emptyOverview(),
      providerHeaders: [
        { text: "Sağlayıcı", value: "name" },
        { text: "Yeni ödeme", value: "new_payments_enabled", align: "center" },
        { text: "Aktif TYA", value: "active_cluster_count", align: "end" },
        { text: "Başarı oranı", value: "success_rate", align: "end" },
        {
          text: "Başarılı işlem",
          value: "successful_payment_count",
          align: "end",
        },
        { text: "Karttan çekilen", value: "charged_amount", align: "end" },
        {
          text: "Bekleyen / inceleme",
          value: "attention_count",
          align: "center",
        },
        { text: "Son başarı", value: "last_success_at" },
      ],
      attentionHeaders: [
        { text: "Ödeme / Deneme", value: "payment_id" },
        { text: "TYA", value: "cluster_name" },
        { text: "Sağlayıcı", value: "payment_provider_name" },
        { text: "Tutar", value: "amount", align: "end" },
        { text: "Durum", value: "monitoring_status" },
        { text: "Son sağlayıcı sonucu", value: "last_provider_event" },
      ],
    };
  },
  computed: {
    financialMetrics() {
      const value = this.overview.financial_summary || {};

      return [
        {
          label: "Karttan çekilen",
          value: value.charged_amount,
          money: true,
          caption: `${value.successful_payment_count || 0} başarılı ödeme`,
          icon: "mdi-credit-card-outline",
          color: "primary",
          footLabel: "Başarılı ödeme",
          footValue: value.successful_payment_count || 0,
        },
        {
          label: "TYA'lara aktarılacak",
          value: value.expected_hakedis_amount,
          money: true,
          caption: "Robosay hesabına göre",
          icon: "mdi-office-building-outline",
          color: "success",
          footLabel: "Sağlayıcı teyidi",
          footValue: "Bu metrikte yok",
        },
        {
          label: "Robosay Komisyonu",
          value: value.robosay_commission_amount,
          money: true,
          caption: "Başarılı ödemelerde Robosay payı",
          icon: "mdi-percent-outline",
          color: "info",
          footLabel: "Dönem bazında",
          footValue: "Başarılı ödemeler",
        },
        {
          label: "Sağlayıcı maliyeti",
          value: value.provider_cost_amount,
          money: true,
          caption: "Kayıtlı sağlayıcı maliyeti",
          icon: "mdi-receipt-text-outline",
          color: "warning",
          footLabel: "Tutar kaynağı",
          footValue: "Robosay",
        },
      ];
    },
    paymentStatuses() {
      const value = this.overview.payment_status || {};

      return [
        {
          key: "all",
          label: "Tümü",
          value: value.total_count || 0,
          caption: "Oluşturulan ödemeler",
          icon: "mdi-view-grid-outline",
          color: "primary",
        },
        {
          key: "success",
          label: "Başarılı",
          value: value.successful_count || 0,
          caption: "Tahsilat tamamlandı",
          icon: "mdi-check-circle-outline",
          color: "success",
        },
        {
          key: "failed",
          label: "Başarısız",
          value: value.failed_count || 0,
          caption: "Ödeme alınamadı",
          icon: "mdi-close-circle-outline",
          color: "error",
        },
        {
          key: "attention",
          label: "Takip gereken",
          value: value.attention_count || 0,
          caption: "Bekleyen veya inceleme",
          icon: "mdi-alert-circle-outline",
          color: "warning",
        },
      ];
    },
    periodLabel() {
      const period = this.overview.meta?.period || {};
      return period.min && period.max ? `${period.min} - ${period.max}` : "-";
    },
  },
  mounted() {
    this.syncUrlState();
    this.loadOverview();
  },
  methods: {
    applyFilters() {
      this.syncUrlState();
      this.loadOverview();
    },
    syncUrlState() {
      syncPaymentMonitoringUrl(this.filters);
    },
    loadOverview() {
      if (this.isLoading) return;
      this.isLoading = true;

      this.$api
        .query("api-super-admin/payment-monitoring/overview", {
          params: buildPaymentMonitoringParams(this.filters),
        })
        .then((response) => {
          this.overview = response.data.data || emptyOverview();
        })
        .catch((error) => this.handleError(error))
        .finally(() => (this.isLoading = false));
    },
    percent: formatMonitoringPercent,
    formatDate: formatMonitoringDate,
    statusMeta(status) {
      return (
        {
          pending: { text: "Bekleyen", color: "warning" },
          unknown: { text: "Sonuç belirsiz", color: "info" },
          manual_review: { text: "İnceleme gerekli", color: "error" },
        }[status] || { text: status || "-", color: "grey" }
      );
    },
  },
};
</script>

<style lang="scss">
@import "./payment-monitoring.scss";

.payment-status-panel {
  background: #ffffff !important;
}

.payment-status-grid > .col {
  border-right: 1px solid var(--payment-monitoring-line-soft);
}

.payment-status-grid > .col:last-child {
  border-right: 0;
}

.payment-status-item {
  position: relative;
  min-height: 104px;
  padding: 15px;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.payment-status-item::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: var(--payment-monitoring-primary);
  content: "";
}

.payment-status-item--success::before {
  background: #1bc5bd;
}

.payment-status-item--error::before {
  background: #f64e60;
}

.payment-status-item--warning::before {
  background: #ffa800;
}

@media (max-width: 959px) {
  .payment-status-grid > .col {
    border-right: 0;
    border-bottom: 1px solid var(--payment-monitoring-line-soft);
  }

  .payment-status-grid > .col:last-child {
    border-bottom: 0;
  }
}
</style>
